import type { WebVitalDiagnostics } from "@website/tracking/types/web_vital";
import {
  CLS_DIAGNOSTIC_FIELDS,
  FCP_DIAGNOSTIC_FIELDS,
  INP_DIAGNOSTIC_FIELDS,
  LCP_DIAGNOSTIC_FIELDS,
  TTFB_DIAGNOSTIC_FIELDS
} from "@website/widget/scripts/web-vitals/diagnostic-fields";
import {
  DiagnosticField,
  ExtendedNavigationTiming,
  LayoutShiftEntry,
  LayoutShiftSource,
  Metric
} from "@website/widget/scripts/web-vitals/types";

export type WebVitalDiagnosticsPayload = Partial<WebVitalDiagnostics>;

const getRectArea = (rect?: DOMRectReadOnly): number =>
  rect ? rect.width * rect.height : 0;

function getLargestLayoutShiftSource(
  sources: Array<LayoutShiftSource>
): LayoutShiftSource | undefined {
  // Prefer one likely culprit over sending every shifted node.
  // eslint-disable-next-line unicorn/no-array-reduce
  return sources.reduce<LayoutShiftSource | undefined>((largest, source) => {
    if (!largest) return source;

    const sourceArea =
      getRectArea(source.previousRect) + getRectArea(source.currentRect);
    const largestArea =
      getRectArea(largest.previousRect) + getRectArea(largest.currentRect);

    return sourceArea > largestArea ? source : largest;
  }, undefined);
}

function applyDiagnosticFields<TSource extends object, TOutput extends object>(
  source: TSource,
  attribute: Record<string, unknown>,
  fields: Array<DiagnosticField<TSource, TOutput>>
): void {
  const sourceRecord = source as Record<string, unknown>;

  for (const field of fields) {
    const { attributeKey, compute, sourceKey, transform } = field;

    if (compute) {
      const computedValue = compute(source);

      if (computedValue !== undefined) {
        attribute[attributeKey] = computedValue;
      }

      continue;
    }

    if (!sourceKey || !(sourceKey in sourceRecord)) {
      continue;
    }

    const rawValue = sourceRecord[sourceKey];
    const resolvedValue = transform ? transform(rawValue, source) : rawValue;

    if (resolvedValue !== undefined) {
      attribute[attributeKey] = resolvedValue;
    }
  }
}

function buildDiagnosticPayload<TSource extends object, TOutput extends object>(
  source: TSource,
  fields: Array<DiagnosticField<TSource, TOutput>>
): Partial<TOutput> {
  const attribute: Partial<TOutput> = {};

  applyDiagnosticFields<TSource, TOutput>(
    source,
    attribute as Record<string, unknown>,
    fields
  );

  return attribute;
}

export function getDetailedDiagnostics(
  metric: Metric
): WebVitalDiagnosticsPayload {
  const entries = metric.entries || [];

  if (entries.length === 0) return {};

  switch (metric.name) {
    case "LCP": {
      const [entry] = entries;
      if (!entry) return {};

      return buildDiagnosticPayload(entry, LCP_DIAGNOSTIC_FIELDS);
    }

    case "FCP": {
      const [entry] = entries as Array<PerformancePaintTiming>;
      if (!entry) return {};

      return buildDiagnosticPayload(entry, FCP_DIAGNOSTIC_FIELDS);
    }

    case "CLS": {
      // Collapse CLS to the worst shift and its biggest source to keep payloads readable.
      // eslint-disable-next-line unicorn/no-array-reduce
      const largestShift = (entries as Array<LayoutShiftEntry>).reduce<
        LayoutShiftEntry | undefined
      >(
        (largest, entry) =>
          !largest || (entry.value || 0) > (largest.value || 0)
            ? entry
            : largest,
        undefined
      );

      const culpritSource = getLargestLayoutShiftSource(
        largestShift?.sources || []
      );

      return buildDiagnosticPayload(
        { culpritSource, largestShift },
        CLS_DIAGNOSTIC_FIELDS
      );
    }

    case "INP": {
      let worstEntry = entries[0] as PerformanceEventTiming;

      for (const event of entries as Array<PerformanceEventTiming>) {
        if ((event.duration || 0) > (worstEntry.duration || 0)) {
          worstEntry = event;
        }
      }

      if (!worstEntry) return {};

      return buildDiagnosticPayload(worstEntry, INP_DIAGNOSTIC_FIELDS);
    }

    case "TTFB": {
      const [entry] = entries as Array<ExtendedNavigationTiming>;
      if (!entry) return {};

      return buildDiagnosticPayload(entry, TTFB_DIAGNOSTIC_FIELDS);
    }
  }

  return {};
}
